import { useCallback, useRef, useState } from 'react';

/**
 * One in-flight status write per row.
 *
 * WHY THIS EXISTS
 *
 * Every status switch in admin fired its request on change and then waited for
 * a refetch before the thumb moved. For the second or so that took, the switch
 * still looked untouched -- so it got clicked again.
 *
 * The duplicate requests are the visible half. The damaging half is that each
 * click computed its payload from the row the server last sent, which had not
 * changed yet: four rapid clicks on an active plan all send
 * `is_active: false`. An even number of clicks should leave the row where it
 * started; instead it ends up deactivated, and the admin has no idea why.
 *
 * `user_subscriptions.js` already did this correctly with a `statusUpdatingId`.
 * This is that pattern, extracted, so the seven other places share one
 * implementation instead of seven that drift.
 *
 * A per-row id rather than a page-wide `loading` flag: one busy switch must not
 * freeze the rest of the table.
 */
export default function useStatusToggle() {
  const [pendingId, setPendingId] = useState(null);
  const [pendingValue, setPendingValue] = useState(null);

  /**
   * Guards against the burst that a state update cannot.
   *
   * `pendingId` reaches the DOM a render late, so two clicks landing in the
   * same tick both read `null` and both fire. The ref is written synchronously,
   * before React is involved, and is what the guard actually tests.
   */
  const inFlight = useRef(null);

  /**
   * @param {string|number} id     the row being changed
   * @param {boolean} nextValue    what the switch should show straight away
   * @param {() => Promise<any>} request  the write, plus whatever refetch follows
   * @returns {Promise<boolean>} whether the request ran and succeeded
   */
  const run = useCallback(async (id, nextValue, request) => {
    if (inFlight.current !== null) return false;

    inFlight.current = id;
    setPendingId(id);
    setPendingValue(nextValue);

    try {
      await request();
      return true;
    } catch (error) {
      // The caller reports the failure -- each screen has its own toast. All
      // this has to do is let go of the row so the thumb snaps back to whatever
      // the server still says.
      return false;
    } finally {
      inFlight.current = null;
      setPendingId(null);
      setPendingValue(null);
    }
  }, []);

  /** True while this row is being written. Disable the control on it. */
  const isPending = useCallback((id) => pendingId !== null && pendingId === id, [pendingId]);

  /** True while any row is being written. */
  const isBusy = pendingId !== null;

  /**
   * What the switch should show.
   *
   * The optimistic value for the row being written, the server's value for
   * every other row. Nothing in the list is mutated, so a failed write needs no
   * rollback -- the row simply stops being pending and reads from the server
   * again.
   */
  const displayed = useCallback(
    (id, serverValue) => (pendingId !== null && pendingId === id ? pendingValue : serverValue),
    [pendingId, pendingValue],
  );

  return { run, isPending, isBusy, displayed, pendingId };
}
